import { useEffect, useState } from "react";
import { api, apiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";

const EMPTY_FIELD = { name: "", label: "", type: "text", options: [] };

export default function Kategorien() {
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [fields, setFields] = useState([{ ...EMPTY_FIELD }]);

  const load = () => api.get("/categories").then((r) => setCategories(r.data));
  useEffect(() => { load(); }, []);

  const setField = (i, k, v) => {
    const next = [...fields];
    next[i] = { ...next[i], [k]: v };
    setFields(next);
  };

  const save = async () => {
    try {
      const clean = fields.filter((f) => f.name && f.label).map((f) => ({
        ...f, name: f.name.toLowerCase().replace(/[^a-z0-9]+/g, "_"),
        options: typeof f.options === "string" ? f.options.split(",").map((x) => x.trim()).filter(Boolean) : f.options,
      }));
      await api.post("/categories", { name, fields: clean });
      toast.success("Kategorie erstellt.");
      setOpen(false); setName(""); setFields([{ ...EMPTY_FIELD }]); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    await api.delete(`/categories/${id}`);
    toast.success("Kategorie gelöscht.");
    load();
  };

  return (
    <div className="space-y-6" data-testid="kategorien-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-400">Artikel</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Kategorien & Produktmodule</h1>
          <p className="text-sm text-zinc-500 mt-1">Neue Kategorien mit eigenen Feldern – ohne Programmänderung.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="new-category-button"><Plus className="w-4 h-4 mr-1" /> Neue Kategorie</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((c) => (
          <div key={c.id} className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm" data-testid={`category-card-${c.key}`}>
            <div className="flex justify-between items-start">
              <div className="font-display font-medium">{c.name}</div>
              <button onClick={() => remove(c.id)} className="text-zinc-300 hover:text-red-600 transition-colors" data-testid={`category-delete-${c.key}`}><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {c.fields.map((f) => (
                <span key={f.name} className="text-[10px] font-mono bg-zinc-100 border border-zinc-200 px-2 py-0.5 rounded-full text-zinc-600">{f.label}</span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="category-form-dialog">
          <DialogHeader><DialogTitle>Neue Kategorie</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. Kamera" data-testid="category-form-name" className="mt-1.5" /></div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-2">Felder</div>
              <div className="space-y-3">
                {fields.map((f, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end border border-zinc-100 rounded-lg p-3">
                    <div className="col-span-4"><Label>Feldname</Label><Input value={f.name} onChange={(e) => setField(i, "name", e.target.value)} placeholder="optik" className="mt-1 font-mono text-xs" /></div>
                    <div className="col-span-4"><Label>Bezeichnung</Label><Input value={f.label} onChange={(e) => setField(i, "label", e.target.value)} placeholder="Optik" className="mt-1" /></div>
                    <div className="col-span-3"><Label>Typ</Label>
                      <select value={f.type} onChange={(e) => setField(i, "type", e.target.value)} className="mt-1 w-full border border-zinc-200 rounded-lg px-2 py-2 text-sm bg-white">
                        <option value="text">Text</option><option value="number">Zahl</option><option value="select">Auswahl</option>
                      </select>
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <button onClick={() => setFields(fields.filter((_, x) => x !== i))} className="text-zinc-300 hover:text-red-600 p-2"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    {f.type === "select" && (
                      <div className="col-span-12"><Label>Optionen (Komma-getrennt)</Label><Input value={f.options} onChange={(e) => setField(i, "options", e.target.value)} placeholder="A, B, C" className="mt-1" /></div>
                    )}
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={() => setFields([...fields, { ...EMPTY_FIELD }])} className="mt-3" data-testid="category-add-field"><Plus className="w-4 h-4 mr-1" /> Feld hinzufügen</Button>
            </div>
            <Button onClick={save} disabled={!name} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="category-save-button">Kategorie speichern</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
